import { useRef } from 'react'

// Logs which of a component's inputs changed on each render, to find render loops.
// Configuration and how to switch it on in a running build: docs/render-tracing.md

const BUILD_FLAG = process.env.REACT_APP_TRACE

// Whether this build can trace at all. Instruments outside this module check it to
// stay out of the way entirely rather than being switched off at runtime.
export const TRACING_ARMED = BUILD_FLAG != null
    && BUILD_FLAG !== ''
    && BUILD_FLAG !== 'false'

const STORAGE_KEY = 'vipost:trace'

const EVERYTHING = '*'

function parseNames(value) {
    const names = (value ?? '')
        .split(',')
        .map((name) => name.trim())
        .filter(Boolean)

    return new Set(names)
}

function readStoredNames() {
    try {
        return window.localStorage.getItem(STORAGE_KEY)
    }
    catch {
        return null
    }
}

function storeNames(value) {
    try {
        window.localStorage.setItem(STORAGE_KEY, value)
    }
    catch {
        // A blocked localStorage still leaves tracing usable for this session.
    }
}

// `true` arms tracing without naming anything, so nothing logs until asked. That is
// what makes a traced build safe to hand to a customer.
const defaultNames = BUILD_FLAG === 'true' ? '' : BUILD_FLAG

let traced = TRACING_ARMED
    ? parseNames(readStoredNames() ?? defaultNames)
    : new Set()

// Checked per event rather than once, so switching a name on takes effect at once.
export function isTraced(name) {
    if (traced.has(EVERYTHING) || traced.has(name)) {
        return true
    }

    const [namespace] = name.split('.')

    return traced.has(namespace)
}

function useRenderTraceEnabled(name, values) {
    const previous = useRef(null)
    const count = useRef(0)

    count.current += 1

    if (!isTraced(name)) {
        return
    }

    // A thunk lets a hot call site skip building its inputs object when untraced.
    const resolved = (typeof values === 'function' ? values() : values) ?? {}

    const changed = previous.current == null
        ? ['(first render)']
        : Object.keys(resolved).filter((key) => !Object.is(resolved[key], previous.current[key]))

    previous.current = resolved

    console.log(`[trace] ${name} #${count.current}`, changed.join(', ') || '(nothing changed)')
}

function useRenderTraceDisabled() {}

if (TRACING_ARMED && typeof window !== 'undefined') {
    window.setRenderTrace = (names) => {
        const value = names === true ? EVERYTHING : (names ?? '')

        traced = parseNames(value)
        storeNames(value)

        return [...traced]
    }
}

export default TRACING_ARMED ? useRenderTraceEnabled : useRenderTraceDisabled
